import type { GeoPoint } from '../drive/trip-session';

interface TripRouteMapProps {
    route: GeoPoint[];
    label?: string;
}

interface ProjectedPoint {
    x: number;
    y: number;
}

const ROUTE_COLORS = {
    surfaceRaised: 'rgb(var(--color-surface-raised))',
    line: 'rgb(var(--color-line))',
    brand: 'rgb(var(--color-brand))',
    success: 'rgb(var(--color-success))',
    surface: 'rgb(var(--color-surface))',
    muted: 'rgb(var(--color-muted))',
} as const;

export function projectRoute(points: GeoPoint[], width: number, height: number, padding: number): ProjectedPoint[] {
    if (points.length === 0) return [];

    const lats = points.map((p) => p.lat);
    const lngs = points.map((p) => p.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const latSpan = Math.max(maxLat - minLat, 0.00001);
    const lngSpan = Math.max(maxLng - minLng, 0.00001);
    const innerWidth = Math.max(1, width - padding * 2);
    const innerHeight = Math.max(1, height - padding * 2);

    return points.map((p) => ({
        x: padding + ((p.lng - minLng) / lngSpan) * innerWidth,
        y: height - padding - ((p.lat - minLat) / latSpan) * innerHeight,
    }));
}

export function routePath(points: ProjectedPoint[]): string {
    return points
        .map((p, index) => `${index === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
        .join(' ');
}

export function TripRouteMap({ route, label = 'Route' }: TripRouteMapProps) {
    const width = 320;
    const height = 160;
    const points = projectRoute(route, width, height, 18);
    const path = routePath(points);
    const hasPoints = route.length > 0;
    const hasLine = route.length >= 2;
    const last = points.at(-1);
    const first = points[0];

    return (
        <div className="rounded-lg border border-line bg-surface-raised overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 text-xs">
                <span className="font-medium">{label}</span>
                <span className="muted">{route.length} GPS points</span>
            </div>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="block w-full h-40 bg-surface-raised"
                role="img"
                aria-label={hasPoints ? 'Trip route preview' : 'No route points recorded yet'}
            >
                <defs>
                    <pattern id="trip-grid" width="24" height="24" patternUnits="userSpaceOnUse">
                        <path d="M 24 0 L 0 0 0 24" fill="none" stroke={ROUTE_COLORS.line} strokeWidth="1" />
                    </pattern>
                    <linearGradient id="trip-route-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={ROUTE_COLORS.success} />
                        <stop offset="100%" stopColor={ROUTE_COLORS.brand} />
                    </linearGradient>
                </defs>
                <rect width={width} height={height} fill="url(#trip-grid)" />
                {hasPoints ? (
                    <>
                        {hasLine && (
                            <>
                                <path
                                    d={path}
                                    fill="none"
                                    stroke={ROUTE_COLORS.surface}
                                    strokeWidth="9"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    opacity="0.8"
                                />
                                <path
                                    d={path}
                                    fill="none"
                                    stroke="url(#trip-route-gradient)"
                                    strokeWidth="5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </>
                        )}
                        {first && <circle cx={first.x} cy={first.y} r="5" fill={ROUTE_COLORS.success} stroke={ROUTE_COLORS.surface} strokeWidth="2" />}
                        {last && <circle cx={last.x} cy={last.y} r="5" fill={ROUTE_COLORS.brand} stroke={ROUTE_COLORS.surface} strokeWidth="2" />}
                    </>
                ) : (
                    <text
                        x={width / 2}
                        y={height / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={ROUTE_COLORS.muted}
                        fontSize="13"
                    >
                        Start driving to draw your route
                    </text>
                )}
            </svg>
        </div>
    );
}
